/**
 * /admin/trade-hub — Trade Partner Hub (Aug 2026).
 *
 * One-menu operations centre: the whole partner lifecycle as numbered
 * stages an operator can follow top to bottom — marketing → prospects →
 * approval → onboarding → operations → billing → tracking. Each stage
 * card explains what it's for, how to use it, and links straight to the
 * admin page and (where relevant) the partner-facing view, with live
 * counts from GET /api/admin/trade-hub/summary.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Megaphone, Mail, UserCheck, Settings2, Store, Receipt, TrendingUp,
  ArrowUpRight, Eye,
} from "lucide-react";
import api from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const STAGES = (s) => [
  {
    icon: Megaphone,
    title: "Marketing & Recruitment",
    blurb: "The public pages that attract new partners. Share these links in outreach, socials and print.",
    howTo: [
      "Send prospects to the Partner Program page — it answers program questions from the same knowledge base the advisor uses.",
      "The application form is self-serve; submissions land in your approval queue automatically.",
      "Each partner can have a public storefront with their Google reviews — short link /p/{slug} for counter cards and QR codes.",
    ],
    links: [
      { to: "/partners", label: "Partner Program page", external: true, partnerView: true },
      { to: "/trade/register", label: "Application form", external: true, partnerView: true },
      { to: "/admin/partners", label: "Manage storefronts" },
    ],
    counts: [],
  },
  {
    icon: Mail,
    title: "Prospects & Invitations",
    blurb: "Your partner CRM. Add businesses you'd like on board and invite them — one by one or as a campaign.",
    howTo: [
      "Add a prospect, then Send Invite — duplicate protection stops double-sends automatically.",
      "Group invitations into campaigns to compare delivery, opens, clicks and applications.",
      "Open any prospect to see its full email timeline (sent → delivered → opened → clicked → applied).",
    ],
    links: [{ to: "/admin/partner-prospects", label: "Prospects CRM" }],
    counts: [
      { label: "Awaiting invite", value: s?.prospects_uninvited },
      { label: "Invited", value: s?.prospects_invited },
      { label: "Applied", value: s?.prospects_applied },
    ],
  },
  {
    icon: UserCheck,
    title: "Applications & Approval",
    blurb: "Where applications land. Review the business, approve, and switch on exactly what each partner may do.",
    howTo: [
      "Open a partner and use the capability switches: Refer customers, Purchase kits, Retail counter, Invoice account.",
      "PWS franchisees get their own portal; other partners see only what their capabilities allow.",
    ],
    links: [{ to: "/admin/partners", label: "Partners list" }],
    counts: [
      { label: "In pipeline", value: s?.partners_pipeline },
      { label: "Active", value: s?.partners_active },
    ],
  },
  {
    icon: Settings2,
    title: "Onboarding & Setup",
    blurb: "Everything a new partner needs lives on their detail page.",
    howTo: [
      "Set their wholesale pricing and commercial terms.",
      "Copy their referral link + QR code for counter displays.",
      "Attach a Google Place ID to pull their reviews onto their storefront.",
      "The Acquisition History panel shows how they were won (campaign, invite, timeline).",
    ],
    links: [{ to: "/admin/partners", label: "Open a partner" }],
    counts: [],
  },
  {
    icon: Store,
    title: "Day-to-Day Operations",
    blurb: "What partners see and do — useful for support calls and training.",
    howTo: [
      "Retail Stockists serialise kit stock and book counter tests — customer pays them in-store; prices are never shown to the customer.",
      "Kit purchasers reorder stock from Kit Orders.",
      "PWS franchisees use the full Trade Partner portal.",
    ],
    links: [
      { to: "/trade", label: "Trade portal", external: true, partnerView: true },
      { to: "/trade/retail", label: "Retail counter", external: true, partnerView: true },
      { to: "/partner/kit-orders", label: "Kit orders", external: true, partnerView: true },
    ],
    counts: [{ label: "Retail-enabled partners", value: s?.partners_retail }],
  },
  {
    icon: Receipt,
    title: "Billing & Money",
    blurb: "Wholesale invoicing for retail counter sales, and referral commissions.",
    howTo: [
      "Retail Invoices shows uninvoiced counter charges per partner — Generate Invoice produces the numbered PDF; Mark Paid closes it.",
      "Commissions tracks referral earnings awaiting payout.",
    ],
    links: [
      { to: "/admin/retail-invoices", label: "Retail Invoices" },
      { to: "/admin/commissions", label: "Commissions" },
    ],
    counts: [
      { label: "Partners to invoice", value: s?.retail_uninvoiced_partners },
      { label: "Uninvoiced", value: s ? money(s.retail_uninvoiced_cents) : undefined },
      { label: "Unpaid invoices", value: s?.retail_unpaid_invoices },
      { label: "Commissions pending", value: s?.commissions_pending },
    ],
  },
  {
    icon: TrendingUp,
    title: "Tracking & Growth",
    blurb: "How well is recruitment working? The acquisition funnel ties campaigns to signed partners.",
    howTo: [
      "The funnel shows prospects → invited → applied → active, with per-campaign rollups.",
      "Conversion attribution links each new partner back to the campaign and invitation that won them.",
    ],
    links: [{ to: "/admin/partner-acquisition", label: "Acquisition dashboard" }],
    counts: [
      { label: "Campaigns", value: s?.campaigns },
      { label: "Converted", value: s?.prospects_applied },
    ],
  },
];

function StageLink({ link }) {
  const cls = link.partnerView
    ? "inline-flex items-center gap-1 text-[12px] font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 ring-1 ring-slate-200 hover:bg-slate-200 transition-colors"
    : "inline-flex items-center gap-1 text-[12px] font-semibold px-2.5 py-1 rounded-full bg-cyan-50 text-cyan-800 ring-1 ring-cyan-200 hover:bg-cyan-100 transition-colors";
  const inner = (
    <>
      {link.partnerView && <Eye className="w-3 h-3" />}
      {link.label}
      {link.partnerView && <span className="text-[10px] font-normal text-slate-500">partner view</span>}
      <ArrowUpRight className="w-3 h-3" />
    </>
  );
  return link.external ? (
    <a href={link.to} target="_blank" rel="noreferrer noopener" className={cls}>{inner}</a>
  ) : (
    <Link to={link.to} className={cls}>{inner}</Link>
  );
}

export default function TradeHub() {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get("/admin/trade-hub/summary").then(({ data }) => setSummary(data)).catch(() => {});
  }, []);

  const stages = STAGES(summary);

  return (
    <PortalShell
      title="Trade Partner Hub"
      subtitle="The whole partner workflow in one place — follow the stages top to bottom, from marketing to money."
    >
      <div className="space-y-4" data-testid="trade-hub-page">
        {/* Workflow strip */}
        <div className="flex flex-wrap items-center gap-1.5 text-[11px] font-semibold text-slate-500" data-testid="trade-hub-strip">
          {stages.map((st, i) => (
            <span key={st.title} className="inline-flex items-center gap-1.5">
              <span className="px-2 py-0.5 rounded-full bg-slate-100 ring-1 ring-slate-200 text-slate-700">
                {i + 1}. {st.title}
              </span>
              {i < stages.length - 1 && <span className="text-slate-300">→</span>}
            </span>
          ))}
        </div>

        {stages.map((st, i) => {
          const Icon = st.icon;
          return (
            <section
              key={st.title}
              className="rounded-2xl border border-slate-200 bg-white p-5"
              data-testid={`trade-hub-stage-${i + 1}`}
            >
              <div className="flex flex-wrap items-start gap-4">
                <div className="flex items-center gap-3 min-w-[260px]">
                  <div className="w-10 h-10 rounded-xl bg-cyan-50 ring-1 ring-cyan-100 flex items-center justify-center text-cyan-700">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-400">Stage {i + 1}</div>
                    <h2 className="text-base md:text-lg font-bold text-slate-900">{st.title}</h2>
                  </div>
                </div>
                {st.counts.length > 0 && (
                  <div className="flex flex-wrap gap-2 ml-auto">
                    {st.counts.map((c) => (
                      <div key={c.label} className="px-3 py-1.5 rounded-xl bg-slate-50 ring-1 ring-slate-200 text-center">
                        <div className="text-sm font-bold text-slate-900">{c.value ?? "—"}</div>
                        <div className="text-[10px] text-slate-500">{c.label}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <p className="mt-3 text-sm text-slate-600">{st.blurb}</p>
              <ul className="mt-2 space-y-1">
                {st.howTo.map((h) => (
                  <li key={h} className="text-[13px] text-slate-600 flex gap-2">
                    <span className="text-cyan-600 mt-0.5">•</span>{h}
                  </li>
                ))}
              </ul>
              <div className="mt-3 flex flex-wrap gap-2">
                {st.links.map((l) => <StageLink key={l.to + l.label} link={l} />)}
              </div>
            </section>
          );
        })}
      </div>
    </PortalShell>
  );
}
